import { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FilterOperations } from '../../../ducks/filter/index';
import { getQueryParams } from '../../../helpers/utils';

/* Tenemos 4 tipos de filtros: filtros que necesitan traducción, filtros que no necesitan traducción, Filtros con key compuestos y Filtros booleanos */

const facetsWithCompositeKey = ['realEstateType', 'province', 'city', 'zone', 'publicationPlanId'];

const useUrlSearchParams = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [formatedUrlFilters, setFormatedUrlFilters] = useState([]);
  const availableFacets = [
    'status',
    'publicationPlan',
    'publicationPlanId',
    'operationType',
    'branches',
    'publishedBy',
    'realEstateType',
    'province',
    'city',
    'zone',
    'searchCode',
    'sort',
    'onlineFirst',
    'hasDuplicates',
    'hasReports',
    'hasReportsTotal',
    'hasQuality'
  ];

  /* Tenemos dos tipos de filtros que se van pisando entre ellos porque comparten la pill y lugar en los params: 
  por ubicación (province, city y zone) y por plan (publicationPlan y publicationPlanId) */
  const locationFilters = ['province', 'city', 'zone'];
  const planFilters = ['publicationPlan', 'publicationPlanId'];

  function formatSearchParams() {
    const params = getQueryParams();

    const stringToObjectParams = [];
    availableFacets.forEach(item => {
      if (params.has(item)) {
        stringToObjectParams.push({ facetName: item, key: params.get(item) });
      }
    });

    return stringToObjectParams;
  }

  function filterUpdateFormat(key, filterName) {
    // Se arma objeto con el filtro que será aplicado por el dispatch y que luego se mostrará en la pill.

    /* Los filtros de 'province', 'city', 'zone', 'publicationPlan' y 'publicationPlanId' se van pisando porque comparten la pill. 
    Por eso el filtro que será pisado se restablece a los valores del inicialState { id: '', displayName: '' } para no mostralo en la pill */
    if (filterName === 'city') {
      return {
        [filterName]: {
          id: key[0],
          displayName: key[1]
        },
        province: {
          id: '',
          displayName: ''
        }
      };
    }
    if (filterName === 'zone') {
      return {
        [filterName]: {
          id: key[0],
          displayName: key[1]
        },
        city: {
          id: '',
          displayName: ''
        }
      };
    }
    if (filterName === 'publicationPlanId') {
      return {
        [filterName]: {
          id: key[0],
          displayName: key[1]
        },
        publicationPlan: {
          id: '',
          displayName: ''
        }
      };
    }
    if (filterName === 'hasDuplicates') {
      return {
        [filterName]: {
          id: 'true',
          displayName: 'duplicated'
        }
      };
    }
    if (filterName === 'hasQuality') {
      return {
        [filterName]: {
          id: 'true',
          displayName: 'quality'
        }
      };
    }
    if (filterName === 'hasReports') {
      return {
        [filterName]: {
          id: 'true',
          displayName: 'reports'
        }
      };
    }
    if (filterName === 'hasReportsTotal') {
      return {
        [filterName]: {
          id: 'true',
          displayName: 'reportsTotal'
        }
      };
    }
    if (filterName === 'branches') {
      return {
        [filterName]: {
          id: key,
          displayName: key
        }
      };
    }
    if (filterName === 'publishedBy') {
      return {
        [filterName]: {
          id: key,
          displayName: key
        }
      };
    }
    return {
      [filterName]: {
        id: key[0],
        displayName: facetsWithCompositeKey.includes(filterName) ? key[1] : key[0]
      }
    };
  }

  const facetsPreventSplit = ['branches', 'publishedBy'];

  const chargeAppliedFilters = useCallback(() => {
    const filtersObj = {};
    if (!formatedUrlFilters) return;
    formatedUrlFilters.forEach(filter => {
      // se agrega una opcion para branches por posibles caracteres especiales (,)
      Object.assign(
        filtersObj,
        filterUpdateFormat(
          facetsPreventSplit.includes(filter.facetName) ? filter.key : filter.key.split(','),
          filter.facetName
        )
      );
    });
    dispatch(FilterOperations.update(filtersObj));
  }, [dispatch, facetsPreventSplit, formatedUrlFilters]);

  useEffect(() => {
    setFormatedUrlFilters(formatSearchParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  /**
   * La función limpia el filtro seleccionado.
   * @param {string} filterName es el nombre del filtro a aplicar
   */
  const deleteUrlFilter = filterName => {
    const params = getQueryParams();

    // se valida si es un filtro por ubicación y se limpia:
    if (locationFilters.includes(filterName)) {
      locationFilters.forEach(filter => {
        params.delete(filter);
      });
      // se valida si es un filtro por tipo de plan y se limpia:
    } else if (planFilters.includes(filterName)) {
      planFilters.forEach(filter => {
        params.delete(filter);
      });
      // caso contrario se limpia de los filtros:
    } else {
      params.delete(filterName);
    }

    history.push({ search: params.toString() });
  };

  /**
   * La función limpia todos los filtros existentes.
   */
  const cleanUrlFilters = () => {
    const params = getQueryParams();

    // se valida si alguno de los facets posibles está aplicado en la url para luego eliminarlo:
    availableFacets.forEach(filterName => {
      if (params.has(filterName)) {
        params.delete(filterName);
      }
    });
    history.push({ search: params.toString() });
  };

  /**
   * La función limpia todos los filtros existentes y aplica el nuevo filtro.
   * @param {string | Array} key es el valor del filtro a aplicar
   * @param {string} filterName es el nombre del filtro a aplicar
   */
  const cleanPartialUrlFilters = (key, filterName) => {
    const params = getQueryParams();

    // se limpian todos los filtros existentes:
    availableFacets.forEach(_filterName => {
      if (params.has(_filterName)) {
        params.delete(_filterName);
      }
    });

    // se carga el filtro entrante:
    const joinedKey = Array.isArray(key) ? key.join(',') : key;
    params.append(filterName, joinedKey);

    history.push({ search: params.toString() });
  };

  /**
   * La función aplica el filtro deseado. A diferencia de la función cleanPartialUrlFilters(), se mantienen los demás filtros aplicados.
   * @param {string | Array} key es el valor del filtro a aplicar
   * @param {string} filterName es el nombre del filtro a aplicar
   */
  const setUrlSearch = (key, filterName) => {
    const params = getQueryParams();
    const hasFilter = params.has(filterName);
    const joinedKey = Array.isArray(key) ? key.join(',') : key;

    // validamos si es un filtro por ubicación para eliminarlo dado que se van a ir pisando entre ellos (province, city y zone):
    if (locationFilters.includes(filterName)) {
      locationFilters.forEach(filter => {
        params.delete(filter);
      });
    }
    // validamos si es un filtro por tipo de plan para eliminarlo dado que se van a ir pisando entre ellos (publicationPlan y publicationPlanId):
    if (planFilters.includes(filterName)) {
      planFilters.forEach(filter => {
        params.delete(filter);
      });
    }

    // Se valida si ya está aplicado el filtro para no duplicarlo en la url:
    if (hasFilter) {
      params.set(filterName, joinedKey);
    } else {
      params.append(filterName, joinedKey);
    }

    history.push({
      search: params.toString()
    });
  };

  return {
    formatedUrlFilters,
    deleteUrlFilter,
    cleanUrlFilters,
    setUrlSearch,
    chargeAppliedFilters,
    filterUpdateFormat,
    availableFacets,
    cleanPartialUrlFilters
  };
};

export default useUrlSearchParams;
